import { useMemo, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { Link } from 'react-router-dom';
import AnswerContainer from './answer-container';
import FeedbackPanel from './feedback-panel';
import NovartisLiveChatButton from './novartis-live-chat-button';
import SourcesMenu from './sources-menu';
import { saveFeedback } from '../../api/save-feedback';
import { Feedback, IMessage, Modals, ReferenceType } from '../../common/types';
import { Pages } from '../../constants/pages';
import useIsMobile from '../../hooks/use-is-mobile';
import { cn } from '../../lib/utils';
import { CURRENT_INSTANCE } from '../../tenant';
import MarkdownRenderer from '../common/markdown-renderer';
import { Button } from '../ui/button';
import VideoPlayerDialog from '../video/video-player-dialog';

interface AnswerMessageProps {
  message: IMessage;
  onShowMoreAnswers: (messageId: number) => void;
  showModal: (type: Modals, userQuery?: string) => void;
  hasMoreOptions: boolean | undefined;
  userQuery?: string;
}

function AnswerMessage(props: AnswerMessageProps) {
  const { t } = useTranslation();
  const isMobile = useIsMobile();
  const [feedbackSent, setFeedbackSent] = useState<boolean>(false);
  const [selectedVideo, setSelectedVideo] = useState<{
    docId: string;
    title: string;
  } | null>(null);
  const chatClasses = CURRENT_INSTANCE.theme.classes?.chat;
  const isHexal = CURRENT_INSTANCE.id === 'hexal';
  const isNovartis = CURRENT_INSTANCE.id === 'novartis';

  async function handleFeedback(feedback: Feedback) {
    if (props.message.queryId && props.message.questionId) {
      await saveFeedback({
        queryId: props.message.queryId,
        questionId: props.message.questionId,
        value: feedback,
      });
      setFeedbackSent(true);
    }
  }

  const textMessage = props.message.message || '';
  const referencesWithExternalLinks = useMemo(() => {
    return Array.from(
      new Map(
        (props.message.references || []).map((item) => [item.sourceDocumentId, item])
      ).values()
    ).filter((ref) => ref.externalDocumentLink.trim().length > 0);
  }, [props.message.references]);
  const hasReferenceLinks = referencesWithExternalLinks.length > 0;

  const references = useMemo(() => {
    return (props.message.references || []).map((ref, index) => {
      const { name, sourceDocumentId, headerId, headerTitle, referenceType, externalDocumentLink } =
        ref;
      let path = `${Pages.PDFViewer}/${sourceDocumentId}`;

      if (ReferenceType.Web === referenceType) {
        path = externalDocumentLink;
      } else if ([ReferenceType.Smpc, ReferenceType.Leaflet].includes(referenceType)) {
        path = `${path}?header-id=${headerId}`;
      } else if (ReferenceType.Video === referenceType) {
        path = `${Pages.VideoViewer}/${sourceDocumentId}`;
      }

      const shouldPrefixType = [
        ReferenceType.Smpc,
        ReferenceType.Leaflet,
        ReferenceType.Video,
      ].includes(referenceType);
      const linkName = `${shouldPrefixType ? t(`common.${referenceType}`) + ': ' : ''}${name}${headerTitle ? `, ${headerTitle}` : ''}`;

      if (ReferenceType.Video === referenceType && !isHexal) {
        return (
          <div key={index}>
            <button
              type="button"
              className={cn(
                'cursor-pointer border-0 bg-transparent p-0 text-left underline underline-offset-[3px] transition-colors',
                isNovartis
                  ? 'text-[#FF585D] hover:text-[#002068]'
                  : 'text-[#2563eb] hover:text-[#1d4ed8]'
              )}
              onClick={() => setSelectedVideo({ docId: sourceDocumentId, title: linkName })}
            >
              {linkName}
            </button>
          </div>
        );
      }

      return (
        <div key={index}>
          <Link to={path} target={ReferenceType.Web === referenceType ? '_blank' : '_self'}>
            {linkName}
          </Link>
        </div>
      );
    });
  }, [isHexal, isNovartis, props.message, t]);

  return (
    <div className="mb-5">
      <AnswerContainer className={cn(chatClasses?.responseMessage)}>
        {props.message.questionQuery && (
          <div>
            <i>
              <p>
                {t('chat.response.found_answer_for', {
                  found_question: props.message.questionQuery,
                })}
              </p>
            </i>
          </div>
        )}

        <MarkdownRenderer content={textMessage} />
        <div className="mt-4 space-y-0.5">{references}</div>
        <footer className="flex flex-wrap justify-between items-center gap-4 mt-5">
          <div />
          <section className="flex justify-between items-center gap-6">
            {props.hasMoreOptions && (
              <Button
                className={cn(
                  'bg-[hsl(var(--xircle-main))] text-xs hover:bg-[hsl(var(--xircle-secondary))]',
                  chatClasses?.alternativeButton
                )}
                onClick={() => props.onShowMoreAnswers(props.message.id)}
              >
                {t('chat.alternative_answers')}
              </Button>
            )}
            {!feedbackSent && <FeedbackPanel submit={handleFeedback} />}
          </section>
        </footer>
      </AnswerContainer>
      <section
        className={cn(
          'w-full flex flex-col sm:flex-row sm:items-center gap-2 sm:gap-3 sm:px-1',
          hasReferenceLinks ? 'sm:w-3/5' : 'sm:w-fit'
        )}
      >
        <Button
          className={cn(
            'bg-[hsl(var(--xircle-secondary))] w-full',
            !hasReferenceLinks && 'sm:w-auto',
            chatClasses?.followUpButton
          )}
          size={isMobile ? 'sm' : 'default'}
          onClick={() => props.showModal(Modals.MedicalInfo, props.userQuery)}
        >
          {t('chat.info_service')}
        </Button>
        <NovartisLiveChatButton inline query={props.userQuery} size={isMobile ? 'sm' : 'default'} />
        <SourcesMenu references={referencesWithExternalLinks} />
      </section>
      <VideoPlayerDialog
        docId={selectedVideo?.docId ?? null}
        title={selectedVideo?.title ?? t('common.video')}
        open={Boolean(selectedVideo)}
        onOpenChange={(open) => {
          if (!open) {
            setSelectedVideo(null);
          }
        }}
      />
    </div>
  );
}

export default AnswerMessage;
